import { useId, type ReactNode } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatDateBR } from "@/lib/format";
import { cn } from "@/lib/utils";

interface OpenToWorkRingProps {
  /** Última leitura do LinkedIn (sql/294): true liga o anel; false e null (nunca lido) não mostram nada. */
  openToWork?: boolean | null;
  observedAt?: string | null;
  className?: string;
  children: ReactNode;
}

// Arco da faixa no lado esquerdo, de baixo para cima, sobre um círculo de raio 42 num viewBox 100x100.
const BAND_PATH = "M57.29 91.36 A42 42 0 0 1 35.64 10.53";

/** Anel verde com "#OPENTOWORK" em volta do avatar, como no LinkedIn. */
export function OpenToWorkRing({ openToWork, observedAt, className, children }: OpenToWorkRingProps) {
  const pathId = useId();
  if (!openToWork) return <>{children}</>;

  const seenOn = observedAt ? `, visto em ${formatDateBR(observedAt)}` : "";
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn("relative inline-flex shrink-0", className)}>
            {children}
            <svg
              viewBox="0 0 100 100"
              className="pointer-events-none absolute inset-0 size-full text-tone-success-fg"
              aria-hidden="true"
            >
              <defs>
                <path id={pathId} d={BAND_PATH} />
              </defs>
              <circle cx="50" cy="50" r="47" fill="none" stroke="currentColor" strokeWidth="6" />
              <use href={`#${pathId}`} fill="none" stroke="currentColor" strokeWidth="16" strokeLinecap="round" />
              <text className="fill-white" fontSize="11" fontWeight="700" letterSpacing="0.5" dominantBaseline="central">
                <textPath href={`#${pathId}`} startOffset="50%" textAnchor="middle">
                  #OPENTOWORK
                </textPath>
              </text>
            </svg>
            <span className="sr-only">Open to Work</span>
          </span>
        </TooltipTrigger>
        <TooltipContent>Open to Work no LinkedIn{seenOn}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
